import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import { useImportWizardStore } from './store/useImportWizardStore';
import WizardStepper from './components/WizardStepper';
import { FileUploadStep } from './components/FileUploadStep';
import { FileAnalysisStep } from './components/FileAnalysisStep';
import { MappingConfigStep } from './components/MappingConfigStep';
import { ValidationStep } from './components/ValidationStep';
import { ImportProgressStep } from './components/ImportProgressStep';
import { ResultsStep } from './components/ResultsStep';

/**
 * Custom navigation blocker hook for react-router v6 with BrowserRouter.
 * Intercepts both in-app navigation (via history) and browser navigation (beforeunload).
 */
function useNavigationBlocker(shouldBlock: boolean) {
  const [isBlocked, setIsBlocked] = useState(false);
  const proceedRef = useRef<(() => void) | null>(null);
  const { navigator } = React.useContext(UNSAFE_NavigationContext);

  useEffect(() => {
    if (!shouldBlock) return;

    // Intercept in-app navigation by wrapping the navigator's push and replace
    const originalPush = navigator.push;
    const originalReplace = navigator.replace;

    navigator.push = (...args: Parameters<typeof originalPush>) => {
      proceedRef.current = () => {
        navigator.push = originalPush;
        navigator.replace = originalReplace;
        originalPush(...args);
      };
      setIsBlocked(true);
    };

    navigator.replace = (...args: Parameters<typeof originalReplace>) => {
      proceedRef.current = () => {
        navigator.push = originalPush;
        navigator.replace = originalReplace;
        originalReplace(...args);
      };
      setIsBlocked(true);
    };

    // Handle browser navigation (refresh, close tab, back button)
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock, navigator]);

  const proceed = useCallback(() => {
    setIsBlocked(false);
    if (proceedRef.current) {
      proceedRef.current();
      proceedRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    setIsBlocked(false);
    proceedRef.current = null;
  }, []);

  return { isBlocked, proceed, reset };
}

const BulkLeadImport: React.FC = () => {
  const currentStep = useImportWizardStore((s) => s.currentStep);
  const mappingConfig = useImportWizardStore((s) => s.mappingConfig);
  const stepContentRef = useRef<HTMLDivElement>(null);

  // Responsive breakpoints for layout adjustments
  const isMobile = useMediaQuery('(max-width:767px)');

  // Block navigation when there are unsaved mapping changes on the mapping step
  const hasUnsavedChanges =
    Object.keys(mappingConfig).length > 0 && currentStep === 'mapping';

  const { isBlocked, proceed, reset } = useNavigationBlocker(hasUnsavedChanges);

  // Focus management: move focus to step content area when step changes
  useEffect(() => {
    if (stepContentRef.current) {
      stepContentRef.current.focus();
    }
  }, [currentStep]);

  const renderStep = () => {
    switch (currentStep) {
      case 'upload':
        return <FileUploadStep />;
      case 'analysis':
        return <FileAnalysisStep />;
      case 'mapping':
        return <MappingConfigStep />;
      case 'validation':
        return <ValidationStep />;
      case 'progress':
        return <ImportProgressStep />;
      case 'results':
        return <ResultsStep />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        px: isMobile ? 1 : 0,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Typography
        variant={isMobile ? 'h6' : 'h5'}
        sx={{ mb: isMobile ? 2 : 3 }}
      >
        Bulk Lead Import
      </Typography>

      {/* Wizard Stepper */}
      <Box sx={{ mb: isMobile ? 2 : 3 }}>
        <WizardStepper />
      </Box>

      {/* Step content with focus management and ARIA live region */}
      <Box
        ref={stepContentRef}
        tabIndex={-1}
        aria-live="polite"
        sx={{ outline: 'none' }}
      >
        {renderStep()}
      </Box>

      {/* Navigation guard dialog */}
      <Dialog
        open={isBlocked}
        onClose={reset}
        aria-labelledby="nav-guard-dialog-title"
        aria-describedby="nav-guard-dialog-description"
      >
        <DialogTitle id="nav-guard-dialog-title">Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText id="nav-guard-dialog-description">
            You have unsaved mapping configuration changes. Are you sure you
            want to leave? Your changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={reset} color="primary">
            Stay
          </Button>
          <Button onClick={proceed} color="error">
            Leave
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkLeadImport;
